import React from 'react'
import styled from 'styled-components'
import { MdError, MdFileDownload, MdSearch } from 'react-icons/md'

import Colors from '../../commons/Colors'
import Dropdown from '../../commons/components/Dropdown'
import RoundButton from '../../commons/components/RoundButton'
import OutlineButton from '../../commons/components/OutlineButton'
import AutoCompleteAsync from '../../commons/components/AutoCompleteAsync'
import BasicDatePickerCalendar from '../../commons/components/DatePickerCalendar'
import { Label } from '../../commons/commonStyles'

export const MAX_PERIOD_HINT =
    '* O período máximo permitido para consulta é de 12 meses.'

export const URGENCY_OPTIONS = [
    { label: 'Todos', value: 'ALL' },
    { label: 'Urgentes', value: 'URGENT' },
    { label: 'Não urgentes', value: 'NOT_URGENT' },
]

export default function DetailedOrderReportFilters({
    period,
    periodError,
    urgency,
    setUrgency,
    selectedSellers,
    setSelectedSellers,
    selectedClients,
    setSelectedClients,
    isLoading,
    isExporting,
    handlePeriodChange,
    handleSearch,
    handleExport,
    handleClearFilters,
    fetchSellers,
    fetchClients,
}) {
    const handleMultiSelection = (event, setFunction) =>
        setFunction(event || [])

    const isSelected = (item, selectedItems, key) =>
        selectedItems.some(selected => selected.value?.[key] === item?.[key])

    return (
        <Container>
            <FiltersGrid>
                <Field>
                    <FieldLabel>Data inicial *</FieldLabel>
                    <DatePickerCalendar
                        placeholder="De"
                        name="initialDate"
                        id="initialDate"
                        placeholderColor={Colors.orange}
                        dateIconColor={Colors.orange}
                        isInvalidDate={Boolean(periodError)}
                        value={period.initialDate}
                        onChange={handlePeriodChange}
                        showButtonBar
                        todayButtonClassName="today-button"
                    />
                </Field>

                <Field>
                    <FieldLabel>Data final *</FieldLabel>
                    <DatePickerCalendar
                        placeholder="Até"
                        name="finalDate"
                        id="finalDate"
                        placeholderColor={Colors.orange}
                        dateIconColor={Colors.orange}
                        isInvalidDate={Boolean(periodError)}
                        value={period.finalDate}
                        onChange={handlePeriodChange}
                        showButtonBar
                        todayButtonClassName="today-button"
                    />
                </Field>

                <Field>
                    <FieldLabel>Vendedor</FieldLabel>
                    <AutoCompleteAsync
                        isMulti
                        useOrangeMode
                        useCustomInput
                        zIndex={5}
                        menuDataTestId="sellersFilter"
                        placeholder="Todos os vendedores"
                        customInputText={{
                            singular: 'vendedor selecionado',
                            plural: 'vendedores selecionados',
                        }}
                        value={selectedSellers}
                        fetchSuggestions={fetchSellers}
                        onChange={event =>
                            handleMultiSelection(event, setSelectedSellers)
                        }
                        getSelectedOption={item =>
                            isSelected(item, selectedSellers, '_id')
                        }
                    />
                </Field>

                <Field>
                    <FieldLabel>Cliente</FieldLabel>
                    <AutoCompleteAsync
                        isMulti
                        useOrangeMode
                        useCustomInput
                        zIndex={5}
                        menuDataTestId="clientsFilter"
                        placeholder="Todos os clientes"
                        customInputText={{
                            singular: 'cliente selecionado',
                            plural: 'clientes selecionados',
                        }}
                        value={selectedClients}
                        fetchSuggestions={fetchClients}
                        onChange={event =>
                            handleMultiSelection(event, setSelectedClients)
                        }
                        getSelectedOption={item =>
                            isSelected(item, selectedClients, 'id')
                        }
                    />
                </Field>

                <Field>
                    <FieldLabel>Urgência</FieldLabel>
                    <Dropdown
                        name="urgency"
                        ariaLabelledBy="urgency"
                        value={urgency}
                        options={URGENCY_OPTIONS}
                        optionLabel="label"
                        optionValue="value"
                        onChange={event => setUrgency(event.value)}
                    />
                </Field>
            </FiltersGrid>

            <PeriodHint>{MAX_PERIOD_HINT}</PeriodHint>

            {periodError && (
                <PeriodErrorBanner role="alert" data-testid="period-error">
                    <MdError size={22} color={Colors.heavyRed} />
                    {periodError}
                </PeriodErrorBanner>
            )}

            <ActionsContainer>
                <ClearButton
                    color={Colors.brownishGrey}
                    testId="clear-filters-button"
                    onClick={handleClearFilters}
                >
                    Limpar filtros
                </ClearButton>

                <ExportButton
                    color={Colors.orange}
                    testId="export-button"
                    disabled={Boolean(periodError)}
                    loading={isExporting}
                    onClick={handleExport}
                >
                    <MdFileDownload size={16} />
                    Exportar para Excel
                </ExportButton>

                <SearchButton
                    dataTestId="search-button"
                    disabled={Boolean(periodError)}
                    loading={isLoading}
                    onClick={handleSearch}
                >
                    <MdSearch size={16} />
                    Consultar
                </SearchButton>
            </ActionsContainer>
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    border: solid 1px ${Colors.lightBlueGreyFour};
    border-radius: 4px;
    padding: 16px;
    margin-bottom: 16px;
`

const FiltersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    column-gap: 16px;
`

const Field = styled.div`
    display: flex;
    flex-direction: column;
`

const FieldLabel = styled(Label)`
    margin-bottom: 8px;
`

const PeriodHint = styled.span`
    font-size: 12px;
    font-style: italic;
    color: ${Colors.brownishGrey};
    margin-top: 8px;
`

const PeriodErrorBanner = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
    padding: 12px 16px;
    border: solid 1px ${Colors.opacityRed};
    border-radius: 4px;
    background-color: #fdeeee;
    color: ${Colors.heavyRed};
    font-size: 14px;
`

const ActionsContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
`

const ClearButton = styled(OutlineButton)`
    display: flex;
    align-items: center;
`

const ExportButton = styled(OutlineButton)`
    display: flex;
    align-items: center;
    gap: 6px;
`

const SearchButton = styled(RoundButton)`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background-color: ${Colors.orange};
    border-color: ${Colors.orange};
`

const DatePickerCalendar = styled(BasicDatePickerCalendar)`
    .p-inputtext {
        border-radius: 2px;
    }

    .p-button {
        border-radius: 2px;
    }

    .today-button {
        display: none;
    }
`
